/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Typography, Input, Select, Modal, message, Upload, Button as AntdButton } from 'antd';
import { ShoppingOutlined, DownloadOutlined, UploadOutlined, StockOutlined, DeleteOutlined } from '@ant-design/icons';
import { Package, Plus, X, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Checkbox, Spinner, Header, SelectCardWithMultipleInputs, ConfirmationModal } from '@crc-evans/ui-components';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import './ConsumablesLog.css';
import { mergeProjectMaterialUnits } from '../../constants/projectMaterialUnitOptions';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const filterActiveConsumableMaster = (list) =>
  (Array.isArray(list) ? list : []).filter((item) => {
    const status = String(item?.status || 'active').trim().toLowerCase();
    return status !== 'inactive';
  });

/** Align API / form values to Critical | Medium | Normal for display and dashboard. */
function normalizeConsumableCriticality(v) {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  const low = s.toLowerCase();
  if (low === 'critical') return 'Critical';
  if (low === 'medium') return 'Medium';
  if (low === 'normal') return 'Normal';
  return s;
}

/** % Remaining = remaining / initial stock × 100 (clamped 0–100). */
function calcPercentRemaining(remaining, initialStock) {
  const rem = parseFloat(remaining);
  const init = parseFloat(initialStock);
  if (!Number.isFinite(init) || init <= 0 || !Number.isFinite(rem)) return null;
  const pct = (rem / init) * 100;
  return Math.max(0, Math.min(100, pct));
}

function formatPercentRemaining(remaining, initialStock) {
  const pct = calcPercentRemaining(remaining, initialStock);
  if (pct == null) return '—';
  return `${pct.toFixed(1)}%`;
}

/** Extract created project record from POST /project-material (handles `[{...}, false]` shapes). */
function parseProjectMaterialCreateResponse(response) {
  const raw = response?.data?.data ?? response?.data;
  if (Array.isArray(raw)) {
    return raw.find((item) => item && typeof item === 'object' && item.id != null) || null;
  }
  if (raw && typeof raw === 'object' && raw.id != null) {
    return raw;
  }
  return null;
}

function buildProjectMaterialPayload({
  category,
  name,
  productCode,
  projectId,
  initialQuantity,
  safetyStock,
  unit,
}) {
  return {
    name,
    category,
    productCode,
    batchNumber: productCode || 'NA',
    manufactureName: '',
    totalMobStockQty: initialQuantity,
    projectId,
    safetyStock,
    unit,
    reminder: '',
  };
}

// Local Component: CommentBox
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '40px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

// Single-select for modal dropdowns – same styling as Downtime Option / Quality Root
// valueKey/labelKey: which field to use from option objects (e.g. 'category', 'name', 'productCode', 'unit')
// openUp: when true, dropdown panel opens upward (for bottom-of-modal fields so list isn't clipped)
const CustomSingleSelect = ({ value, options = [], onChange, placeholder = 'Select...', disabled = false, valueKey, labelKey, openUp = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const opts = Array.isArray(options) ? options : [];
  const displayLabel = value || '';

  const getOptValue = (opt) => {
    if (typeof opt === 'string') return opt;
    if (valueKey && opt[valueKey] != null) return String(opt[valueKey]);
    return opt.value ?? opt.label ?? opt.name ?? opt.category ?? opt.unit ?? opt.productCode ?? '';
  };
  const getOptLabel = (opt) => {
    if (typeof opt === 'string') return opt;
    if (labelKey && opt[labelKey] != null) return String(opt[labelKey]);
    return opt.label ?? opt.name ?? opt.category ?? opt.unit ?? opt.productCode ?? opt.value ?? '';
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0, textAlign: 'left' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          minHeight: '38px',
          height: '38px',
          boxSizing: 'border-box',
          padding: '4px 12px',
          fontSize: '13px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: disabled ? '#F9FAFB' : '#fff',
          color: displayLabel ? '#374151' : '#9CA3AF',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          opacity: disabled ? 0.6 : 1,
          overflow: 'hidden'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          ...(openUp
            ? { bottom: 'calc(100% + 4px)', top: 'auto' }
            : { top: 'calc(100% + 4px)' }),
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '8px',
          maxHeight: '200px',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        >
          {opts.map((opt, index) => {
            const optValue = getOptValue(opt);
            const optLabel = getOptLabel(opt);
            return (
              <div
                key={index}
                onClick={() => { onChange(optValue); setIsOpen(false); }}
                style={{
                  padding: '8px 10px',
                  fontSize: '13px',
                  color: '#374151',
                  backgroundColor: value === optValue ? '#F3F4F6' : 'transparent',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === optValue ? '#F3F4F6' : 'transparent'}
              >
                {optLabel}
              </div>
            );
          })}
          {opts.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '13px', color: '#9CA3AF' }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
};

const StyledDeleteIcon = () => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        cursor: 'pointer',
        color: isHovered ? '#EF4444' : '#D1D5DB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.2s ease'
      }}
    >
      <Trash2 size={18} />
    </div>
  );
};

const ConsumablesLog = ({
  consumablesData: initialConsumablesData = {},
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  projectId,
  isShiftSkipped = false,
  isLastDprSection
}) => {
  const [consumablesRows, setConsumablesRows] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const dispatch = useDispatch();
  const skipConsumables = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipConsumables || false;
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newConsumable, setNewConsumable] = useState({
    category: '',
    name: '',
    productCode: '',
    initialQuantity: '',
    safetyStock: '',
    unit: '',
    criticality: ''
  });

  const CRITICALITY_OPTIONS = ['Critical', 'Medium', 'Normal'];
  const [consumableMaster, setConsumableMaster] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [nameOptions, setNameOptions] = useState([]);
  const [productCodeOptions, setProductCodeOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [isMasterLoading, setIsMasterLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [addModalError, setAddModalError] = useState('');
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);

  const handleSkipConsumablesChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipConsumables', false));
    }
  };

  const handleConfirmSkipConsumables = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipConsumables', true));
    setIsSkipConfirmOpen(false);
  };

  // Fetch consumables data from API when shift changes
  const fetchConsumablesData = useCallback(async () => {
    if (!dprLogId) return; // Don't fetch if dprLogId is not available
    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-consumable?dprLogId=${dprLogId}&shift=${shift}`);

      console.log('Consumables API Response:', response);
      console.log('Response data:', response.data);

      // Handle response structure: response.data.data contains the actual data
      let consumableEntries = null;
      let notesData = null;
      let reportNoData = null;

      if (response.data) {
        if (response.data.data && response.data.data.consumableEntries) {
          consumableEntries = response.data.data.consumableEntries;
          notesData = response.data.data.notes;
          reportNoData = response.data.data.reportNo;
        } else if (response.data.consumableEntries) {
          consumableEntries = response.data.consumableEntries;
          notesData = response.data.notes;
          reportNoData = response.data.reportNo;
        }
      }

      // Store reportNo
      if (reportNoData) {
        setReportNo(reportNoData);
      }

      console.log('Extracted consumableEntries:', consumableEntries);

      if (consumableEntries && Array.isArray(consumableEntries) && consumableEntries.length > 0) {
        // Map API response to consumablesRows format
        let mappedRows = consumableEntries.map((entry, index) => {
          const refillRaw =
            entry.refillQuantity != null && entry.refillQuantity !== ''
              ? entry.refillQuantity
              : entry.refill_qty != null && entry.refill_qty !== ''
                ? entry.refill_qty
                : '';

          return {
            id: `consumable-${index}-${entry.id || Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            itemName: entry.consumableName || '',
            productCode: entry.productCode || '',
            category: entry.category || '',
            safetyStock: entry.safetyStock ?? 0,
            unit: entry.unit || '',
            criticality: normalizeConsumableCriticality(
              entry.criticality || entry.consumableCriticality || entry.priority || ''
            ),
            month: entry.monthUsed || 0,
            total: entry.initialStock || 0,
            initialStock: entry.initialStock || 0,
            usedToday: entry.todayUsed || 0,
            remaining: entry.remainingStock || 0,
            originalRemainingStock: entry.remainingStock || 0,
            originalTodayUsed: entry.todayUsed || 0,
            availableStock: (parseFloat(entry.remainingStock) || 0) + (parseFloat(entry.todayUsed) || 0),
            projectConsumableId: entry.projectConsumableId,
            refillQuantity: refillRaw === '' || refillRaw == null ? '' : refillRaw,
            originalEntry: entry
          };
        });

        // Merge criticality from project consumables when DPR entry omits it (set on Project → Consumables or DPR Settings).
        if (projectId) {
          try {
            const pres = await axiosConfig.get(`/project-consumables?projectId=${projectId}`);
            const raw = pres.data?.data;
            const projList = Array.isArray(raw) ? raw : raw?.rows || [];
            if (Array.isArray(projList) && projList.length > 0) {
              const byProjectConsumableId = new Map();
              const byCodeCat = new Map();
              projList.forEach((c) => {
                const crit = normalizeConsumableCriticality(
                  c.criticality ?? c.consumableCriticality ?? ''
                );
                if (c.id != null && c.id !== '') byProjectConsumableId.set(Number(c.id), crit);
                const code = String(c.productCode || '').trim().toLowerCase();
                const cat = String(c.category || '').trim().toLowerCase();
                if (code || cat) byCodeCat.set(`${code}|${cat}`, crit);
              });
              mappedRows = mappedRows.map((row) => {
                let crit = row.criticality;
                if (!crit && row.projectConsumableId != null && row.projectConsumableId !== '') {
                  crit = byProjectConsumableId.get(Number(row.projectConsumableId)) || '';
                }
                if (!crit) {
                  const k = `${String(row.productCode || '').trim().toLowerCase()}|${String(row.category || '').trim().toLowerCase()}`;
                  crit = byCodeCat.get(k) || '';
                }
                return {
                  ...row,
                  criticality: crit || row.criticality,
                };
              });
            }
          } catch (mergeErr) {
            console.warn('Could not merge consumable criticality from project list:', mergeErr);
          }
        }

        console.log('Mapped rows:', mappedRows);
        setConsumablesRows(mappedRows);
        // Clear validation errors when data is fetched
        setValidationErrors({});

        // Set remarks if available (outside the mappedRows loop)
        const remarksVal = response.data?.data?.notes || response.data?.data?.comments || response.data?.data?.remarks ||
          response.data?.notes || response.data?.comments || response.data?.remarks || '';
        setRemarks(remarksVal);
      } else {
        console.warn('No consumable entries found in API response');
        setConsumablesRows([]);

        // Still try to set remarks even if no entries
        const remarksVal = response.data?.data?.notes || response.data?.data?.comments || response.data?.data?.remarks ||
          response.data?.notes || response.data?.comments || response.data?.remarks || '';
        setRemarks(remarksVal);
      }
    } catch (error) {
      console.error('Error fetching consumables data:', error);
      console.error('Error response:', error.response);
      // On error, keep existing data or use initialConsumablesData
      if (initialConsumablesData.consumablesRows) {
        setConsumablesRows(initialConsumablesData.consumablesRows);
      } else {
        setConsumablesRows([]);
      }
      if (initialConsumablesData.remarks) {
        setRemarks(initialConsumablesData.remarks);
      }
    } finally {
      setIsLoading(false);
    }
  }, [shift, dprLogId, projectId]);

  useEffect(() => {
    fetchConsumablesData();
  }, [fetchConsumablesData]); // Re-fetch when shift changes

  // Max usage = stock at shift start + REFILL QTY for this DPR row
  const maxUsableForRow = (row) => {
    const availableStock = parseFloat(row.availableStock) || 0;
    const refill = parseFloat(row.refillQuantity) || 0;
    return availableStock + refill;
  };

  // Validate all rows whenever consumablesRows changes (but preserve existing errors from handleRowChange)
  useEffect(() => {
    const errors = {};
    consumablesRows.forEach(row => {
      const usedToday = parseFloat(row.usedToday) || 0;
      const maxUsable = maxUsableForRow(row);

      if (usedToday > maxUsable) {
        errors[row.id] = `Used today (${usedToday}) cannot exceed available stock plus refill (${maxUsable})`;
      }
    });

    // Only update if there are errors or if we need to clear existing errors
    setValidationErrors(prevErrors => {
      // Merge with previous errors to preserve any that might have been set in handleRowChange
      const mergedErrors = { ...prevErrors, ...errors };
      // Remove any errors that are no longer valid
      Object.keys(mergedErrors).forEach(key => {
        if (!errors[key] && mergedErrors[key]) {
          delete mergedErrors[key];
        }
      });
      return mergedErrors;
    });
  }, [consumablesRows]);

  // Update local state when prop changes (fallback)
  useEffect(() => {
    if (initialConsumablesData.consumablesRows && consumablesRows.length === 0) {
      setConsumablesRows(initialConsumablesData.consumablesRows);
    }
    if (initialConsumablesData.remarks && !remarks) {
      setRemarks(initialConsumablesData.remarks);
    }
  }, [initialConsumablesData]);

  const buildConsumableEntriesPayload = (rowsOverride) => {
    const rows = rowsOverride ?? consumablesRows;
    const consumableEntries = rows.map((row) => {
      const originalEntry = row.originalEntry || {};
      const refillQ = parseFloat(row.refillQuantity);
      const refillQuantity = Number.isFinite(refillQ) ? refillQ : 0;
      // Refill date is owned by the backend (set on update); client only sends quantity
      const refillFields = { refillQuantity };

      const projectConsumableId =
        row.projectConsumableId ?? originalEntry.projectConsumableId ?? null;

      if (originalEntry.id) {
        return {
          id: originalEntry.id,
          ...(projectConsumableId != null ? { projectConsumableId } : {}),
          consumableName: row.itemName || '',
          todayUsed: row.usedToday || 0,
          productCode: row.productCode || '',
          category: row.category || originalEntry.category,
          safetyStock: row.safetyStock ?? originalEntry.safetyStock,
          unit: row.unit || originalEntry.unit,
          initialStock: row.initialStock || 0,
          remainingStock: row.remaining || 0,
          ...(row.criticality ? { criticality: row.criticality } : {}),
          ...refillFields,
        };
      }
      return {
        ...(projectConsumableId != null ? { projectConsumableId } : {}),
        consumableName: row.itemName || '',
        todayUsed: row.usedToday || 0,
        productCode: row.productCode || '',
        category: row.category || '',
        safetyStock: row.safetyStock ?? 0,
        unit: row.unit || '',
        initialStock: row.initialStock || 0,
        remainingStock: row.remaining || 0,
        ...(row.criticality ? { criticality: row.criticality } : {}),
        ...refillFields,
      };
    });

    return consumableEntries;
  };

  const handleSave = async () => {
    if (isShiftSkipped) return false;
    if (!dprLogId) {
      message.warning('Cannot save: DPR log not loaded.');
      return false;
    }
    setIsSaving(true);
    try {
      // Build consumableEntries array
      const consumableEntries = buildConsumableEntriesPayload();

      // Build payload
      const payload = {
        shift: shift,
        dprConsumable: {
          dprLogId: dprLogId,
          reportNo: reportNo || `DPR-${dprLogId}-C-v1`,
          notes: remarks || '',
          comments: remarks || '',
          consumableEntries: consumableEntries
        }
      };

      console.log('Saving consumables report with payload:', payload);

      // Make PUT request
      await axiosConfig.put('/dpr-consumable', payload);

      // Refetch data to update UI
      await fetchConsumablesData();

      // Call parent onSave callback if provided
      if (onSave) {
        onSave({
          consumablesRows,
          remarks
        });
      }
      message.success('Consumables saved successfully.');
      return true;
    } catch (error) {
      console.error('Error saving consumables report:', error);
      message.error(error?.response?.data?.message || error?.message || 'Failed to save consumables.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddConsumable = () => {
    setAddModalError('');
    setIsModalVisible(true);
    if (consumableMaster.length === 0) {
      fetchConsumableMaster();
    }
  };

  const fetchConsumableMaster = async () => {
    setIsMasterLoading(true);
    try {
      const response = await axiosConfig.get('/consumable-master/all');
      const masterData = response.data?.data || response.data || [];
      const masterList = filterActiveConsumableMaster(
        Array.isArray(masterData) ? masterData : []
      );
      setConsumableMaster(masterList);

      const uniqueCategory = new Set();
      const categoryList = masterList.filter((item) => {
        const value = item?.category;
        if (!value || uniqueCategory.has(value)) {
          return false;
        }
        uniqueCategory.add(value);
        return true;
      });
      setCategoryOptions(categoryList);

      const uniqueUnit = new Set();
      const unitList = masterList.filter((item) => {
        const value = item?.unit;
        if (!value || uniqueUnit.has(value)) {
          return false;
        }
        uniqueUnit.add(value);
        return true;
      });
      setUnitOptions(mergeProjectMaterialUnits(unitList));
    } catch (error) {
      console.error('Error fetching consumable master:', error);
      message.error('Failed to load consumable master data.');
    } finally {
      setIsMasterLoading(false);
    }
  };

  const handleCategoryChange = (value) => {
    setAddModalError('');
    setNewConsumable((prev) => ({
      ...prev,
      category: value || '',
      name: '',
      productCode: '',
      unit: ''
    }));

    if (!value) {
      setNameOptions([]);
      setProductCodeOptions([]);
      return;
    }

    const filtered = consumableMaster.filter((item) => item?.category === value);
    const uniqueName = new Set();
    const names = filtered.filter((item) => {
      const name = item?.name;
      if (!name || uniqueName.has(name)) {
        return false;
      }
      uniqueName.add(name);
      return true;
    });
    setNameOptions(names);
    setProductCodeOptions([]);
  };

  const handleNameChange = (value) => {
    setAddModalError('');
    setNewConsumable((prev) => ({
      ...prev,
      name: value || '',
      productCode: '',
      unit: ''
    }));

    if (!value) {
      setProductCodeOptions([]);
      return;
    }

    const filtered = consumableMaster.filter((item) => item?.name === value && item?.category === newConsumable.category);
    const uniqueCode = new Set();
    const codes = filtered.filter((item) => {
      const code = item?.productCode;
      if (!code || uniqueCode.has(code)) {
        return false;
      }
      uniqueCode.add(code);
      return true;
    });
    setProductCodeOptions(codes);
  };

  const handleProductCodeChange = (value) => {
    setAddModalError('');
    setNewConsumable((prev) => {
      const match = consumableMaster.find(
        (item) =>
          item?.productCode === value &&
          item?.name === prev.name &&
          item?.category === prev.category,
      );
      return {
        ...prev,
        productCode: value || '',
        unit: value ? (match?.unit || '') : '',
      };
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setAddModalError('');
    setNewConsumable({
      category: '',
      name: '',
      productCode: '',
      initialQuantity: '',
      safetyStock: '',
      unit: '',
      criticality: ''
    });
    setNameOptions([]);
    setProductCodeOptions([]);
  };

  const handleAddNewConsumable = async () => {
    if (
      !newConsumable.category ||
      !newConsumable.name ||
      !newConsumable.productCode ||
      newConsumable.initialQuantity === '' ||
      newConsumable.safetyStock === '' ||
      !newConsumable.unit
    ) {
      message.warning('Please fill all required fields.');
      return;
    }

    if (!projectId) {
      message.error('Project ID is missing. Unable to add consumable.');
      return;
    }

    // Frontend duplicate check: same product (productCode) already in the list
    const newCode = (newConsumable.productCode || '').trim().toLowerCase();
    const isDuplicate = (consumablesRows || []).some(
      (row) => (row.productCode || '').trim().toLowerCase() === newCode
    );
    if (isDuplicate) {
      setAddModalError('This product is already in the consumables list. Please choose a different product or edit the existing entry.');
      return;
    }
    setAddModalError('');

    const initialQuantityValue = parseFloat(newConsumable.initialQuantity) || 0;
    const safetyStockValue = parseFloat(newConsumable.safetyStock) || 0;

    try {
      const payload = buildProjectMaterialPayload({
        category: newConsumable.category,
        name: newConsumable.name,
        productCode: newConsumable.productCode,
        projectId,
        initialQuantity: initialQuantityValue,
        safetyStock: safetyStockValue,
        unit: newConsumable.unit,
      });

      const createResponse = await axiosConfig.post('/project-material', payload);
      const created = parseProjectMaterialCreateResponse(createResponse);
      if (!created?.id) {
        message.error('Material was created but no project id was returned.');
        return;
      }

      const projectConsumableId = created.id;
      const criticality =
        normalizeConsumableCriticality(newConsumable.criticality || created.criticality) || '';

      const newRow = {
        id: `consumable-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        projectConsumableId,
        itemName: newConsumable.name.trim(),
        productCode: newConsumable.productCode.trim(),
        category: newConsumable.category || '',
        safetyStock: safetyStockValue,
        unit: newConsumable.unit || '',
        criticality,
        month: 0,
        total: 0,
        initialStock: initialQuantityValue,
        usedToday: 0,
        remaining: initialQuantityValue,
        originalRemainingStock: initialQuantityValue,
        availableStock: initialQuantityValue,
        refillQuantity: '',
        originalEntry: {},
      };

      const updatedRows = [...consumablesRows, newRow];
      setConsumablesRows(updatedRows);

      if (dprLogId) {
        setIsSaving(true);
        try {
          const consumableEntries = buildConsumableEntriesPayload(updatedRows);
          await axiosConfig.put('/dpr-consumable', {
            shift,
            dprConsumable: {
              dprLogId,
              reportNo: reportNo || `DPR-${dprLogId}-C-v1`,
              notes: remarks || '',
              comments: remarks || '',
              consumableEntries,
            },
          });
          await fetchConsumablesData();
        } catch (dprError) {
          console.error('Error linking consumable to DPR log:', dprError);
          message.warning(
            'Consumable added to project but failed to save to this DPR log. Use Save to retry.'
          );
        } finally {
          setIsSaving(false);
        }
      }

      message.success('Consumable added successfully.');
      handleModalCancel();
    } catch (error) {
      console.error('Error adding consumable:', error);
      message.error('Failed to add consumable. Please try again.');
    }
  };

  const handleDeleteRow = (id) => {
    setRowToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete;

    try {
      // Find the row to delete
      const rowToDeleteLocal = consumablesRows.find(row => String(row.id) === String(id));

      // Remove the row from state
      const updatedRows = consumablesRows.filter(row => {
        if (!row || (row.id === undefined && row.id !== 0)) {
          return true; // Keep rows without IDs (shouldn't happen)
        }
        return String(row.id) !== String(id);
      });

      setConsumablesRows(updatedRows);
      setIsDeleteModalOpen(false);
      setRowToDelete(null);

      // Call API immediately with updated data
      if (dprLogId) {
        setIsSaving(true);
        try {
          const consumableEntries = buildConsumableEntriesPayload(updatedRows);

          // Build payload
          const payload = {
            shift: shift,
            dprConsumable: {
              dprLogId: dprLogId,
              reportNo: reportNo || `DPR-${dprLogId}-C-v1`,
              notes: remarks || '',
              comments: remarks || '',
              consumableEntries: consumableEntries
            }
          };

          await axiosConfig.put('/dpr-consumable', payload);
          message.success('Consumable entry deleted successfully');

          // Refetch data to update UI
          await fetchConsumablesData();
        } catch (error) {
          console.error('Error deleting consumable entry:', error);
          message.error('Failed to delete consumable entry');
          // Revert state on error
          setConsumablesRows(consumablesRows);
        } finally {
          setIsSaving(false);
        }
      }
    } catch (error) {
      console.error('Error in delete confirmation:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Consumables');

      // Define headers based on Add Consumables modal fields
      const headerTitles = [
        'Category',
        'Name',
        'Product Code',
        'Initial Quantity',
        'Safety Stock',
        'Unit',
        'Criticality (Critical / Medium / Normal)',
      ];

      worksheet.addRow(headerTitles);
      worksheet.getRow(1).font = { bold: true };
      worksheet.columns = headerTitles.map((title) => ({
        header: title,
        width: Math.max(18, String(title || '').length + 4)
      }));
      const critColIndex = 7;
      for (let r = 2; r <= 500; r += 1) {
        worksheet.getRow(r).getCell(critColIndex).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['"Critical,Medium,Normal"'],
          showErrorMessage: true,
          errorTitle: 'Criticality',
          error: 'Choose Critical, Medium, or Normal.',
        };
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'consumables_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
      message.success('Template downloaded successfully');
    } catch (error) {
      console.error('Template download error:', error);
      message.error('Failed to download template. Please try again.');
    }
  };

  const handleUploadExcel = () => {
    setIsUploadModalVisible(true);
  };

  const handleUploadModalCancel = () => {
    setIsUploadModalVisible(false);
    setUploadFileList([]);
  };

  const handleUploadExcelFile = async () => {
    if (uploadFileList.length === 0) {
      message.error('Please select an Excel file.');
      return;
    }

    const file = uploadFileList[0].originFileObj || uploadFileList[0];
    if (!file) {
      message.error('Invalid file. Please try again.');
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!rows || rows.length < 2) {
        message.error('The uploaded file has no data rows.');
        return;
      }

      const headers = rows[0].map((h) => String(h || '').trim().toLowerCase());

      // Expected headers mapping
      const headerMapping = {
        category: 'category',
        name: 'name',
        'product code': 'productCode',
        'initial quantity': 'initialQuantity',
        'safety stock': 'safetyStock',
        unit: 'unit',
        criticality: 'criticality',
        'criticality (critical / medium / normal)': 'criticality',
      };

      const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== null && cell !== undefined && cell !== ''));

      const mappedRows = [];
      for (let index = 0; index < dataRows.length; index++) {
        const row = dataRows[index];
        const rowObj = {};

        headers.forEach((header, colIndex) => {
          const mappedKey = headerMapping[header];
          if (mappedKey) {
            const cellValue = row[colIndex];
            rowObj[mappedKey] = cellValue !== null && cellValue !== undefined ? String(cellValue).trim() : '';
          }
        });

        // Validate required fields
        if (!rowObj.category || !rowObj.name || !rowObj.productCode ||
          rowObj.initialQuantity === '' || rowObj.safetyStock === '' || !rowObj.unit) {
          console.warn(`Skipping row ${index + 2}: Missing required fields`, rowObj);
          continue;
        }

        const initialQuantityValue = parseFloat(rowObj.initialQuantity) || 0;
        const safetyStockValue = parseFloat(rowObj.safetyStock) || 0;
        const criticalityValue = normalizeConsumableCriticality(rowObj.criticality);

        // Call API to add each consumable as project material
        try {
          const payload = buildProjectMaterialPayload({
            category: rowObj.category,
            name: rowObj.name,
            productCode: rowObj.productCode,
            projectId,
            initialQuantity: initialQuantityValue,
            safetyStock: safetyStockValue,
            unit: rowObj.unit,
          });

          const createResponse = await axiosConfig.post('/project-material', payload);
          const created = parseProjectMaterialCreateResponse(createResponse);

          mappedRows.push({
            id: `consumable-upload-${Date.now()}-${index}-${Math.random().toString(36).substring(2, 11)}`,
            ...(created?.id != null ? { projectConsumableId: created.id } : {}),
            itemName: rowObj.name,
            productCode: rowObj.productCode,
            category: rowObj.category,
            safetyStock: safetyStockValue,
            unit: rowObj.unit,
            month: 0,
            total: 0,
            usedToday: 0,
            initialStock: initialQuantityValue,
            remaining: initialQuantityValue,
            availableStock: initialQuantityValue,
            refillQuantity: '',
            criticality: criticalityValue || '',
            originalEntry: {},
          });
        } catch (error) {
          console.error(`Error adding consumable from row ${index + 2}:`, error);
          message.warning(`Failed to add consumable: ${rowObj.name}`);
        }
      }

      if (mappedRows.length > 0) {
        const updatedRows = [...consumablesRows, ...mappedRows];
        setConsumablesRows(updatedRows);
        message.success(`Successfully added ${mappedRows.length} consumable(s) from Excel`);

        if (dprLogId) {
          setIsSaving(true);
          try {
            const consumableEntries = buildConsumableEntriesPayload(updatedRows);
            await axiosConfig.put('/dpr-consumable', {
              shift,
              dprConsumable: {
                dprLogId,
                reportNo: reportNo || `DPR-${dprLogId}-C-v1`,
                notes: remarks || '',
                comments: remarks || '',
                consumableEntries,
              },
            });
            await fetchConsumablesData();
          } catch (dprError) {
            console.error('Error linking uploaded consumables to DPR log:', dprError);
            message.warning(
              'Consumables added to project but failed to save to this DPR log. Use Save to retry.'
            );
          } finally {
            setIsSaving(false);
          }
        }
      } else {
        message.warning('No valid rows found in the Excel file');
      }

      setIsUploadModalVisible(false);
      setUploadFileList([]);
    } catch (error) {
      console.error('Excel upload error:', error);
      message.error('Failed to upload Excel file. Please check the format and try again.');
    }
  };

  const uploadProps = {
    accept: '.xls,.xlsx',
    multiple: false,
    fileList: uploadFileList,
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls');
      if (!isExcel) {
        message.error('You can only upload Excel files (.xls, .xlsx)');
        return false;
      }
      setUploadFileList([file]);
      return false;
    },
    onRemove: () => {
      setUploadFileList([]);
    }
  };

  const handleRowChange = (id, field, value) => {
    if (!id) {
      console.warn('handleRowChange called without id');
      return;
    }
    setConsumablesRows(prevRows => {
      // Ensure we only update the specific row with matching ID
      const updatedRows = prevRows.map(row => {
        // Strict equality check to ensure we only update the exact matching row
        if (String(row.id) === String(id)) {
          const updatedRow = { ...row, [field]: value };

          // Remaining = stock at shift start − used today + refill (recorded on this DPR)
          if (field === 'usedToday' || field === 'refillQuantity') {
            const availableStock = parseFloat(updatedRow.availableStock) || 0;
            const usedToday = parseFloat(updatedRow.usedToday) || 0;
            const refill = parseFloat(updatedRow.refillQuantity) || 0;

            let remaining = availableStock - usedToday + refill;
            if (remaining < 0) remaining = 0;
            updatedRow.remaining = remaining;

            if (field === 'usedToday' || field === 'refillQuantity') {
              const maxUsable = availableStock + refill;
              setValidationErrors(prevErrors => {
                const newErrors = { ...prevErrors };

                if (usedToday > maxUsable) {
                  newErrors[id] = `Used today (${usedToday}) cannot exceed available stock plus refill (${maxUsable})`;
                } else {
                  delete newErrors[id];
                }

                return newErrors;
              });
            }
          }

          return updatedRow;
        }
        // Return a new object reference to ensure React detects the change
        return { ...row };
      });
      return updatedRows;
    });
  };


  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}

      {/* Main Container */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              backgroundColor: '#FFF1F2',
              padding: '10px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#E11D48'
            }}>
              <Package size={24} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>
                  Consumables Inventory
                </h3>
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>
                Track daily usage, refills, and remaining stock for this DPR shift.
              </p>
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: 6, fontSize: '12px', textAlign: 'left' }}
              >
                <strong>Criticality</strong> (Critical / Medium / Normal) is set per item on the project{' '}
                <strong>Consumables</strong> tab or under <strong>DPR → Settings → Consumables</strong>. The daily
                dashboard highlights <strong>Critical</strong> items only (e.g. gas, wire, coating).{' '}
                Enter <strong>refill qty</strong> when applicable; the server records refill date when the report is saved.
              </Text>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Checkbox
              label="Skip Consumables"
              checked={skipConsumables}
              onChange={handleSkipConsumablesChange}
              borderColor="#00C9A7"
            />
            <Button
              label="Add Consumable"
              variant="primary"
              size="small"
              icon={Plus}
              onClick={handleAddConsumable}
              style={{ backgroundColor: '#F43F5E', borderColor: '#F43F5E' }}
            />
          </div>
        </div>

        {/* Table Header — same pattern as PersonnelLog: Header component in a wrapper */}
        <div style={{ marginBottom: '16px' }}>
          <Header
            items={[
              { label: 'ITEM NAME', width: '15%' },
              { label: 'CODE', width: '10%', align: 'center' },
              { label: 'CRITICALITY', width: '9%', align: 'center' },
              { label: 'INITIAL STOCK', width: '10%', align: 'center' },
              { label: 'USED QTY', width: '12%', align: 'center' },
              { label: 'REFILL QTY', width: '10%', align: 'center' },
              { label: 'QTY REMAINING', width: '12%', align: 'center' },
              { label: '% REMAINING', width: '14%', align: 'center' },
              { label: 'ACTION', width: '8%', align: 'center' }
            ]}
            size="small"
          />
        </div>

        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {consumablesRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>
              No consumables added.
            </div>
          ) : (
            consumablesRows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E2E8F0',
                  padding: '16px 24px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                {/* Item Name + Tags - 15% */}
                <div style={{ width: '15%', minWidth: '15%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{
                    margin: 0,
                    fontWeight: 600,
                    color: '#1e293b',
                    fontSize: '16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left'
                  }}>
                    {row.itemName || ''}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '6px',
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#475569'
                    }}>
                      <span style={{ color: '#94A3B8', fontWeight: 500 }}>MONTH</span> {parseFloat(row.month || 0).toFixed(1)}
                    </span>
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '6px',
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#475569'
                    }}>
                      <span style={{ color: '#94A3B8', fontWeight: 500 }}>TOTAL</span> {parseFloat(row.total || 0).toFixed(1)}
                    </span>
                  </div>
                </div>

                {/* Code - 10% */}
                <div style={{ width: '10%', minWidth: '10%', display: 'flex', justifyContent: 'center' }}>
                  <input
                    type="text"
                    value={row.productCode || '-'}
                    readOnly
                    style={{
                      width: '80%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#F8FAFC',
                      fontSize: '14px',
                      color: '#475569',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Criticality - 9% */}
                <div style={{ width: '9%', minWidth: '9%', display: 'flex', justifyContent: 'center' }}>
                  {row.criticality ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 700,
                        ...(row.criticality === 'Critical'
                          ? { background: '#FEE2E2', color: '#DC2626' }
                          : row.criticality === 'Medium'
                          ? { background: '#FEF3C7', color: '#D97706' }
                          : { background: '#D1FAE5', color: '#059669' }),
                      }}
                    >
                      {row.criticality}
                    </span>
                  ) : (
                    <span style={{ color: '#94A3B8' }}>—</span>
                  )}
                </div>

                {/* Initial Stock - 10% */}
                <div style={{ width: '10%', minWidth: '10%', display: 'flex', justifyContent: 'center' }}>
                  <input
                    type="text"
                    value={row.initialStock || '0'}
                    readOnly
                    style={{
                      width: '80%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#F8FAFC',
                      fontSize: '14px',
                      color: '#475569',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Used Qty - 12% */}
                <div style={{ width: '12%', minWidth: '12%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="number"
                    value={row.usedToday || 0}
                    onChange={(e) => handleRowChange(row.id, 'usedToday', e.target.value)}
                    style={{
                      width: '80%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${validationErrors[row.id] ? '#EF4444' : '#E2E8F0'}`,
                      backgroundColor: '#FFFFFF',
                      fontSize: '14px',
                      color: '#1e293b',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = validationErrors[row.id] ? '#EF4444' : '#00C9A7'}
                    onBlur={(e) => e.target.style.borderColor = validationErrors[row.id] ? '#EF4444' : '#E2E8F0'}
                  />
                  {validationErrors[row.id] && (
                    <div style={{
                      fontSize: '10px',
                      color: '#EF4444',
                      textAlign: 'center',
                      width: '90%',
                      lineHeight: '1.2'
                    }}>
                      {validationErrors[row.id]}
                    </div>
                  )}
                </div>

                {/* Refill quantity - 10% */}
                <div style={{ width: '10%', minWidth: '10%', display: 'flex', justifyContent: 'center' }}>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={row.refillQuantity === '' || row.refillQuantity == null ? '' : row.refillQuantity}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d*\.?\d*$/.test(v)) {
                        handleRowChange(row.id, 'refillQuantity', v === '' ? '' : v);
                      }
                    }}
                    placeholder="0"
                    style={{
                      width: '90%',
                      padding: '8px 8px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#FFFFFF',
                      fontSize: '13px',
                      color: '#1e293b',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#00C9A7'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#E2E8F0'; }}
                  />
                </div>

                {/* Qty Remaining - 12% */}
                <div style={{ width: '12%', minWidth: '12%', display: 'flex', justifyContent: 'center' }}>
                  <input
                    type="text"
                    value={row.remaining !== undefined && row.remaining !== null ? String(row.remaining) : '0'}
                    readOnly
                    style={{
                      width: '80%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#F8FAFC',
                      fontSize: '14px',
                      color: '#475569',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* % Remaining - 14% */}
                <div style={{ width: '14%', minWidth: '14%', display: 'flex', justifyContent: 'center' }}>
                  <input
                    type="text"
                    value={formatPercentRemaining(row.remaining, row.initialStock)}
                    readOnly
                    style={{
                      width: '80%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: '#F8FAFC',
                      fontSize: '14px',
                      color: '#475569',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Action - 8% */}
                <div style={{ width: '8%', minWidth: '8%', display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={() => handleDeleteRow(row.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94A3B8',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <CommentBox
          header="Consumables Requests / Notes"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter material requests or stock observations..."
        />

        {/* Save Button - Sticky */}
        <div style={{
          position: 'sticky',
          bottom: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '24px',
          zIndex: 10
        }}>
          <Button
            label={
              typeof isLastDprSection === 'boolean'
                ? (isLastDprSection ? 'Save section' : 'Save & Next')
                : 'Save Consumables Report'
            }
            variant="secondary"
            onClick={handleSave}
            disabled={isSubmitting || isSaving || Object.keys(validationErrors).length > 0}
            style={{ paddingLeft: '32px', paddingRight: '32px' }}
          />
        </div>
      </div>

      {/* Add Consumable Modal */}
      {isModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          zIndex: 1000,
          overflowY: 'auto'
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '500px',
            maxHeight: '90vh',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flexShrink: 0
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{
                  backgroundColor: '#FFF1F2',
                  padding: '10px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#E11D48'
                }}>
                  <Package size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    Add Consumable
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                    Enter consumable details
                  </p>
                </div>
              </div>
              <button
                onClick={handleModalCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body - scrollable when content is tall (scrollbar hidden) */}
            <div
              className="consumable-modal-body"
              style={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0
              }}
            >
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Consumable Category <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <CustomSingleSelect
                  value={newConsumable.category || ''}
                  options={categoryOptions}
                  onChange={handleCategoryChange}
                  placeholder="Select Category"
                  disabled={isMasterLoading}
                  valueKey="category"
                  labelKey="category"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <CustomSingleSelect
                  value={newConsumable.name || ''}
                  options={nameOptions}
                  onChange={handleNameChange}
                  placeholder="Select Name"
                  disabled={!nameOptions.length}
                  valueKey="name"
                  labelKey="name"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Product Code <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <CustomSingleSelect
                  value={newConsumable.productCode || ''}
                  options={productCodeOptions}
                  onChange={handleProductCodeChange}
                  placeholder="Select Product Code"
                  disabled={!productCodeOptions.length}
                  valueKey="productCode"
                  labelKey="productCode"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Initial Quantity <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="number"
                  value={newConsumable.initialQuantity}
                  onChange={(e) => setNewConsumable({ ...newConsumable, initialQuantity: e.target.value })}
                  placeholder="Initial Quantity"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Safety Stock <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="number"
                  value={newConsumable.safetyStock}
                  onChange={(e) => setNewConsumable({ ...newConsumable, safetyStock: e.target.value })}
                  placeholder="Safety Stock"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Unit <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <CustomSingleSelect
                  value={newConsumable.unit || ''}
                  options={unitOptions}
                  placeholder="Filled from product code"
                  valueKey="unit"
                  labelKey="unit"
                  openUp
                  disabled
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Criticality
                </label>
                <CustomSingleSelect
                  value={newConsumable.criticality || ''}
                  options={CRITICALITY_OPTIONS}
                  onChange={(v) => setNewConsumable((prev) => ({ ...prev, criticality: v || '' }))}
                  placeholder="Select Criticality"
                />
              </div>
            </div>

            {addModalError && (
              <div style={{
                padding: '12px 24px',
                margin: '0 24px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#B91C1C'
              }}>
                {addModalError}
              </div>
            )}

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #E5E7EB'
            }}>
              <Button
                label="Cancel"
                variant="tertiary"
                onClick={handleModalCancel}
                size="medium"
              />
              <Button
                label="Add Consumable"
                variant="primary"
                onClick={handleAddNewConsumable}
                disabled={!newConsumable.category || !newConsumable.name || !newConsumable.productCode || !newConsumable.unit || (newConsumable.initialQuantity === '' || newConsumable.initialQuantity == null) || (newConsumable.safetyStock === '' || newConsumable.safetyStock == null)}
                size="medium"
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Delete Consumable Entry"
        message="Are you sure you want to delete this consumable entry? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#FF3B30',
            borderColor: '#FF3B30'
          }
        }}
      />
      <ConfirmationModal
        title="Skip Consumables"
        message={`Are you sure you want to skip the Consumables log for ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={isSkipConfirmOpen}
        onConfirm={handleConfirmSkipConsumables}
        onCancel={() => setIsSkipConfirmOpen(false)}
        size="medium"
      />
      {/* Upload Modal */}
      <Modal
        title="Upload Consumables (Excel)"
        open={isUploadModalVisible}
        onCancel={handleUploadModalCancel}
        footer={[
          <AntdButton key="cancel" onClick={handleUploadModalCancel}>
            Cancel
          </AntdButton>,
          <AntdButton key="upload" type="primary" onClick={handleUploadExcelFile}>
            Upload
          </AntdButton>
        ]}
      >
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ color: '#ff4d4f' }} />
          </p>
          <p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default ConsumablesLog;
